import T from 'i18n-react';
import { useRouter } from 'next/router';

import texts from 'Components/texts.json';
import { FEATURES } from 'Context/FeaturesContext/FeaturesContextConsts';
import { useListType, useSetListType } from 'Stores/ListStore/ListStore';
import { AccountCategory, AccountSlugs } from 'Types/account';
import { LIST_TYPES } from 'Utilities/consts';
import useFeatureToggle from 'Utilities/hooks/useFeatureToggle';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import { useAdjustedListType } from 'Utilities/hooks/vehicleList/useAdjustedListType';
import ROUTES from 'Utilities/routes';

T.setTexts(texts);

interface UseNoSaleCTAButtonProps {
	buttonText?: string;
	onCTAButtonClick: () => void;
	shouldShowCTAButton: boolean;
}

const browseVehiclesButtonText = T.translate('vehicleList.browseVehiclesButton')?.toString();
const viewPurchasesButtonText = T.translate('vehicleList.viewPurchasesButton')?.toString();

export const useNoSaleCTAButton = (buttonTextProp: UseNoSaleCTAButtonProps['buttonText']): UseNoSaleCTAButtonProps => {
	const { isLiveSaleEnabled, isSaleActive } = useSaleTimes();
	const router = useRouter();
	const listType = useListType();
	const setListType = useSetListType();
	const adjustedListType = useAdjustedListType({ forceFormatBasedOnTime: true });
	const isRemoveBuyItNowFeatureEnabled = useFeatureToggle(FEATURES.removeBuyItNow);
	const isVehicleListDisabled = useFeatureToggle(FEATURES.showDownloadCSVButtonOutsideList);

	if (isVehicleListDisabled) {
		// eslint-disable-next-line no-empty-function
		return { buttonText: '', onCTAButtonClick: () => {}, shouldShowCTAButton: false };
	}

	const isAuction = listType === LIST_TYPES.auction;
	const isAiCompanion = listType === LIST_TYPES.aiCompanion;
	const isBuyItNow = listType === LIST_TYPES.buyItNow;
	const isActiveSaleSameAsListType = isSaleActive ? isAuction : isBuyItNow;
	const shouldHideLiveSaleButtonWhenDisabled = !isLiveSaleEnabled && isBuyItNow && isSaleActive;
	const isAuctionAndRemoveBuyItNowFeatureEnabled = isRemoveBuyItNowFeatureEnabled && isAuction;
	const buttonText =
		buttonTextProp ||
		(isAuctionAndRemoveBuyItNowFeatureEnabled || isAiCompanion ? viewPurchasesButtonText : browseVehiclesButtonText);

	const setListTypeAction = () => (adjustedListType ? setListType(adjustedListType) : setListType(LIST_TYPES.buyItNow));
	const routeToPurchasesAction = () => router.push(ROUTES.ACCOUNT.as(AccountCategory.PURCHASES, AccountSlugs.COMPLETE));
	const ctaButtonClickAction = () =>
		isAuctionAndRemoveBuyItNowFeatureEnabled ? routeToPurchasesAction() : setListTypeAction();

	return {
		buttonText,
		onCTAButtonClick: () => ctaButtonClickAction(),
		shouldShowCTAButton: isAuctionAndRemoveBuyItNowFeatureEnabled
			? true
			: !isActiveSaleSameAsListType && !shouldHideLiveSaleButtonWhenDisabled,
	};
};
